import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Upload, ShieldCheck, FileText, CheckCircle2, Loader2 } from "lucide-react";
import logo from '../../assets/images/base.png';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useOnboarding } from "@/context/OnboardingContext";
import { apiClient } from "@/lib/api";
import { useProjectUpdates } from "@/hooks/use-project-updates";

export default function OnboardingV2() {
    const navigate = useNavigate();
    const location = useLocation();
    const { data, updateData, resetData } = useOnboarding();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(data.scriptFile);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    
    // SSE removed as we are not processing script immediately
    // const { lastUpdate, isConnected } = useProjectUpdates(data.projectId || null);

    // Clear onboarding data when starting fresh
    useEffect(() => {
        const isOAuthReturn = sessionStorage.getItem("from_oauth_redirect");
        const keepData = (location.state as any)?.keepData;
        
        // Reset data only if NOT an OAuth return AND NOT explicitly told to keep data (e.g. back navigation)
        if (!isOAuthReturn && !keepData) {
            console.log("Resetting onboarding data (New Session)");
            // Reset context data - use resetData from context instead of partial update
            // to ensure ALL fields (Page 2, 3, etc.) are cleared, not just script info
            resetData();
        } else {
            if (keepData) {
                console.log("Keeping onboarding data (Back Navigation)");
            }
            // Clear the flag so next time we land here (e.g. manual nav) it clears
            sessionStorage.removeItem("from_oauth_redirect");
        }
    }, [location.state, updateData]);

    useEffect(() => {
        // Sync local file state with context (including null/reset)
        setFile(data.scriptFile);
    }, [data.scriptFile]);


    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) validateAndSetFile(selectedFile);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files?.[0];
        if (droppedFile) validateAndSetFile(droppedFile);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const validateAndSetFile = (file: File) => {
        // Basic validation
        const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];

        // Note: checking file type strictly might be tricky across browsers/OS for some types, 
        // but this is a good baseline. Extension check is also good.
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const validExtensions = ['pdf', 'docx', 'txt'];

        if (!validExtensions.includes(fileExtension || '')) {
            toast.error("Invalid file format. Please upload PDF, DOCX, or TXT.");
            return;
        }

        // Size limit check could go here (e.g., 10MB)

        setFile(file);
        updateData({ scriptFile: file, scriptFileName: file.name });
    };

    const handleNext = async () => {
        if (!file) {
            toast.error("Please upload a script to continue");
            return;
        }

        try {
            setIsUploading(true);

            // Upload script immediately
            // We pass empty string as projectId since it's not created yet
            const script = await apiClient.uploadScript({
                projectId: "",
                file: file,
                title: file.name
            });

            // Store file and script ID in context
            updateData({
                scriptFile: file,
                scriptFileName: file.name,
                scriptId: script.script_id || script._id || script.id
            });

            // Update current history state to indicate we should keep data if returning
            // We use replace: true so we don't create a new history entry for the current page
            // but simply update its state. Then we navigate to the next page.
            navigate(".", { replace: true, state: { keepData: true } });
            
            setTimeout(() => {
                navigate("/onboarding-v2/page2");
            }, 0);
        } catch (error) {
            console.error("Script upload failed:", error);
            toast.error("Failed to upload script. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">


            <main className="relative z-10 w-full flex flex-col items-center min-h-screen">

                <div className="w-full max-w-5xl px-6 py-12 flex flex-col items-center flex-grow">
                    {/* Logo */}
                    <div className="mb-8 relative">
                        <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
                        <img
                            src={logo}
                            alt="Artie"
                            className="w-48 h-48 object-contain relative z-10"
                        />
                    </div>

                    {/* Header & Introduction */}
                    <div className="text-center max-w-2xl mb-12 animate-in slide-in-from-bottom-4 duration-500">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
                            Hi, I'm Artie.
                        </h1>
                        <div className="space-y-4 text-lg text-[oklch(0.55_0_0)] font-ibm leading-relaxed">
                            <p>
                                I help creators understand the commercial potential of their projects - and connect eligible ones with brand partners ready to invest.
                            </p>
                            <p>
                                If you're working on an <span className="font-bold text-[oklch(0.2686_0_0)]">episodic series, a microdrama, or an independent film</span>, you're in the right place.
                            </p>
                            <p>
                                Whether or not you match with a brand through Artie, you'll walk away with actionable insights to best position your project for commercial success.
                            </p>
                        </div>
                    </div>

                    {/* Three-Step Process */}
                    <div className="w-full mb-12 animate-in slide-in-from-bottom-4 duration-500 delay-100">
                        <h2 className="text-2xl font-bold text-center mb-6 font-cabinet text-[oklch(0.2686_0_0)]">Here's how we'll start</h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Step 1 */}
                            <Card className="p-6 bg-white/80 backdrop-blur border border-[oklch(0.92_0_0)] shadow-sm hover:shadow-md transition-all">
                                <div className="w-10 h-10 rounded-full bg-[oklch(0.97_0.01_60)] flex items-center justify-center mb-4 text-[oklch(0.70_0.18_55)] font-bold">1</div>
                                <h3 className="font-bold text-[oklch(0.2686_0_0)] mb-2 font-cabinet">Initial Assessment</h3>
                                <p className="text-sm text-[oklch(0.55_0_0)] font-ibm">I'll analyze your script and audience signals to estimate what your project could earn from brand partnerships.</p>
                            </Card>

                            {/* Step 2 */}
                            <Card className="p-6 bg-white/80 backdrop-blur border border-[oklch(0.92_0_0)] shadow-sm hover:shadow-md transition-all">
                                <div className="w-10 h-10 rounded-full bg-[oklch(0.97_0.01_60)] flex items-center justify-center mb-4 text-[oklch(0.70_0.18_55)] font-bold">2</div>
                                <h3 className="font-bold text-[oklch(0.2686_0_0)] mb-2 font-cabinet">Professional Review</h3>
                                <p className="text-sm text-[oklch(0.55_0_0)] font-ibm">Our AI works with accredited writers and brand strategists to assess craft and market fit — giving you insights to sharpen your commercial positioning.</p>
                            </Card>

                            {/* Step 3 */}
                            <Card className="p-6 bg-white/80 backdrop-blur border border-[oklch(0.92_0_0)] shadow-sm hover:shadow-md transition-all">
                                <div className="w-10 h-10 rounded-full bg-[oklch(0.97_0.01_60)] flex items-center justify-center mb-4 text-[oklch(0.70_0.18_55)] font-bold">3</div>
                                <h3 className="font-bold text-[oklch(0.2686_0_0)] mb-2 font-cabinet">Packaging & Matching</h3>
                                <p className="text-sm text-[oklch(0.55_0_0)] font-ibm">We help you assemble everything a brand needs to say yes. Our goal is to match you with aligned partners — but either way, you'll be set up to pursue brand investment on your own.</p>
                            </Card>
                        </div>
                    </div>

                    {/* Primary Action Area (Upload Box) */}
                    <Card className="w-full max-w-2xl bg-white shadow-xl border border-[oklch(0.92_0_0)] mb-8 overflow-hidden animate-in slide-in-from-bottom-4 duration-500 delay-200">
                        <div className="p-8 md:p-10 flex flex-col">
                            <h2 className="text-2xl font-bold mb-2 font-cabinet text-[oklch(0.2686_0_0)]">Ready to begin?</h2>
                            <p className="text-[oklch(0.55_0_0)] mb-8 text-sm font-ibm">
                                Scripts give us the most to work with, but any written summary is fine. Pilot, episode, full season, feature — whatever you have. You'll specify the scope on the next page. Pitch deck support coming soon
                            </p>

                            {!file ? (
                                <div
                                    className={`w-full border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${isDragging
                                            ? "border-[oklch(0.70_0.18_55)] bg-[oklch(0.97_0.01_60)]/20"
                                            : "border-[oklch(0.75_0_0)] hover:bg-[oklch(0.98_0_0)] hover:border-[oklch(0.70_0.18_55)]/50"
                                        }`}
                                    onClick={() => fileInputRef.current?.click()}
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                >
                                    <input
                                        type="file"
                                        className="hidden"
                                        ref={fileInputRef}
                                        accept=".pdf,.docx,.txt"
                                        onChange={handleFileSelect}
                                    />
                                    <div className="w-16 h-16 bg-[oklch(0.98_0_0)] rounded-full flex items-center justify-center mb-4 shadow-sm">
                                        <Upload className="w-8 h-8 text-[oklch(0.2686_0_0)]" />
                                    </div>
                                    <p className="text-2xl font-bold mb-2 text-[oklch(0.2686_0_0)]">Drop your script here or Click Upload</p>
                                    <p className="text-sm text-black font-normal mb-6">I'm excited to see what you've created.</p>
                                    <p className="text-sm text-gray-400 mb-4">PDF, DOCX, TXT (Max 180 pages)</p>
                                </div>
                            ) : (
                                <div className="w-full border-2 border-[oklch(0.92_0_0)] bg-[oklch(0.98_0_0)] rounded-xl p-6 flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-white rounded-lg border border-[oklch(0.92_0_0)] flex items-center justify-center">
                                            <FileText className="w-6 h-6 text-[oklch(0.70_0.18_55)]" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-[oklch(0.2686_0_0)] truncate max-w-[200px] sm:max-w-xs">{file.name}</p>
                                            <p className="text-xs text-[oklch(0.55_0_0)]">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            setFile(null);
                                            updateData({ scriptFile: null, scriptFileName: null });
                                        }}
                                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                    >
                                        Change
                                    </Button>
                                </div>
                            )}


                            <div className="mt-6 bg-[oklch(0.98_0_0)] rounded-lg px-4 py-3 flex items-start gap-3 border border-[oklch(0.95_0_0)] w-full">
                                <ShieldCheck className="w-4 h-4 text-[oklch(0.55_0_0)] mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-[oklch(0.55_0_0)] leading-tight">
                                    <span className="font-semibold">What happens to your script?</span> I use your script only to analyze fit, spot opportunities, and generate feedback for you. Your work stays private and is never shared or pitched without your say-so.
                                </p>
                            </div>
                        </div>
                    </Card>

                </div>

                {/* Footer & Navigation */}
                <div className="w-full mt-auto pb-10 px-20">
                    <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
                    <div className="w-full flex items-center justify-between">
                        <div className="flex-1 flex justify-start">
                            <Button
                                variant="ghost"
                                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                                onClick={() => navigate("/")}
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back
                            </Button>
                        </div>

                        {/* Progress Indicators */}
                        <div className="flex gap-2 justify-center">
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.2686_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                        </div>

                        <div className="flex-1 flex justify-end">
                            <Button
                                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleNext}
                                disabled={!file || isUploading}
                            >
                                {isUploading ? (
                                    <>
                                        Uploading... <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        Continue
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

            </main>
        </div>
    );
}